import React, {useEffect, useRef, useState} from 'react';
import Icon from "../Icon";
import styled from "styled-components";
import {useOnClickOutside} from "../../hooks/useOnClickOutside";
import Button from "../Button";
import baseTheme from "../../shared/baseTheme";
import {useMessages} from "../../hooks/useMessages";
import {useRouter} from "next/router";
import {getTheme} from "../../shared/themeUtils";

const FilterContain = styled.div`
  position: relative;

  .close-button {
    position: absolute;
    z-index: 2;
    right: 0;
    height: 100%;
    border-top-right-radius: 24px;
    border-bottom-right-radius: 24px;
    width: ${getTheme("space.4")};
    border: none;
    background: ${getTheme("colors.darkGrey")};
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      background: ${getTheme("colors.darkestGrey")};
    }
  }
`

const FilterItem: any = styled.div`
  position: relative;

  p {
    margin: ${getTheme("space.0")};
  }

  .search-attribute {
    margin: ${getTheme("space.2")} ${getTheme("space.0")};
  }
`;

const FilterHeader = styled.div`
  background-color: ${getTheme("colors.background")};
  border-radius: 24px;
  padding: ${getTheme("space.1")} ${getTheme("space.2")};
  display: flex;
  align-items: center;
  border: 1px solid transparent;
  cursor: pointer;

  .search-icon {
    position: relative;
    bottom: ${getTheme("space.0")};
  }

  &.opened {
    border: 1px solid ${getTheme("colors.border")};
    border-bottom: 1px solid transparent;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  p {
    flex: 1;
  }
`;

const FilterBody = styled.div`
  background-color: ${getTheme("colors.white")};
  border-radius: 24px;
  padding: ${getTheme("space.1")};
  display: flex;
  align-items: center;
  max-height: 50px;
  box-sizing: border-box;
  border: 1px solid ${getTheme("colors.border")};
  cursor: pointer;

  .search-section {
    display: flex;
    align-items: center;
    width: 100%;

    input {
      margin-right: ${getTheme("space.0")};
      width: 100%;
      padding: ${getTheme("space.0")} ${getTheme("space.1")};
      border: none;
      border-radius: ${getTheme("borderRadius")};
      outline: none;
    }

    button {
      display: flex;
      justify-content: center;
      align-items: center;

      .search-icon > div {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .close-icon {
      cursor: pointer;
      display: none;
      margin-left: ${getTheme("space.1")};

      & > div > div {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
`;

type SearchFilterPropsType = {
  name: string,
  value?: any,
  onChangeFn?: any
}

const SearchFilter = (props: SearchFilterPropsType) => {
  const ref = useRef();
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const messagesQuery = useMessages()
  const router = useRouter()

  useOnClickOutside(ref, () => setIsFilterOpen(false));

  const hasSearchTerm = search && search.trim().length > 0;

  useEffect(() => {
    if (router.query.search) {
      setSearch(router.query.search.toString())
    }
  }, [router])


  const handleHeaderClick = () => {
    setIsFilterOpen(!isFilterOpen)
    // wait for dom element to be rendered
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 200)
  }


  const handleSubmit = async () => {
    if (search.length > 0) {
      await router.push({
        query: {
          ...(router.query),
          ...({search: search})
        }
      }, undefined, {shallow: true})
    } else {
      // Remove the 'search' query parameter from the current URL
      const { search, ...query } = router.query;
      await router.push({
        query: {
          ...query,
        }
      }, undefined, {shallow: true})
    }
    setIsFilterOpen(false)
  }


  const handleEnterSubmit = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      await handleSubmit()
    }
  }


  const handleClearSearch = async () => {
    setSearch("")
    if (router.query.search) {
      // Remove the 'search' query parameter from the current URL
      const { search, ...query } = router.query;

      await router.push({
        query: {
          ...query,
        }
      }, undefined, {shallow: true})
    }
  }


  return (
    <FilterContain>
      {hasSearchTerm && !isFilterOpen &&
        <button className={isFilterOpen ? "close-button opened" : "close-button"}
                onClick={handleClearSearch}><Icon variant={"medium"}>close-line</Icon></button>}

      <FilterItem ref={ref}>
        {!isFilterOpen && <FilterHeader onClick={handleHeaderClick} className={isFilterOpen ? "opened" : ""}>

          {!hasSearchTerm && <p>{props.name}</p>}
          {hasSearchTerm && <p>{search}</p>}
          <Icon variant={"small"} className={"search-icon mt-2"}>search-line</Icon>
        </FilterHeader>}

        {isFilterOpen && messagesQuery.data &&
          <FilterBody>
            <div className="search-section">
              <input type="text"
                     placeholder={messagesQuery.data.messages["search-button"]}
                     value={search}
                     onChange={(e: any) => setSearch(e.target.value)}
                     onKeyUp={handleEnterSubmit}
                     ref={inputRef}/>
              <Button variant="transparent" onClick={handleSubmit}>
                <Icon color={baseTheme.colors.secondaryFont} variant="small" className="search-icon">search-line</Icon>
              </Button>
              <Button variant="secondary" className="close-icon" onClick={() => setIsFilterOpen(false)}>
                <Icon variant="xSmall" color={baseTheme.colors.primary}>close-line</Icon>
              </Button>
            </div>
          </FilterBody>
        }
      </FilterItem>
    </FilterContain>
  )
}

export default SearchFilter;